import { skipToken } from "@reduxjs/toolkit/query";
import { automationIdSchema } from "@repo/app-types/entities/automation";
import { automationArtifactIdSchema } from "@repo/app-types/entities/automationArtifact";
import type { AutomationArtifactRecipientType } from "@repo/app-types/entities/automationArtifactRecipient";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import { renderTeammateDisplayName } from "@repo/app-types/entities/teammate";
import { AutomationArtifactContent } from "@repo/ui/automations/automationArtifactContent";
import { formatRunTime } from "@repo/ui/automations/automationPresentation";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { PageWidth } from "@repo/ui/Navigation/pageWidth";
import { GradingReferenceProvider } from "@repo/ui/structuredContent/gradingReferenceContext";
import { PlaybookFeedbackReferenceProvider } from "@repo/ui/structuredContent/playbookFeedbackReferenceContext";
import type { FC } from "react";
import { useMemo } from "react";
import { Navigate, useNavigate, useParams } from "react-router";
import { useAutomationByIdQuery } from "../../api/entities/automation.js";
import { useAutomationArtifactByIdQuery } from "../../api/entities/automationArtifact.js";
import { useAllTeammatesQuery } from "../../api/entities/teammate.js";
import { appPaths } from "../../app/appPaths.js";
import { PageBody, PageLayout } from "../../components/layout/pageLayout.js";
import { GradingReferenceLinkAdapter } from "../../features/teammatePerformance/gradingReferenceLinkAdapter.js";
import { PlaybookFeedbackReferenceLinkAdapter } from "../../features/teammatePerformance/playbookFeedbackReferenceLinkAdapter.js";

/**
 * Dedicated page for a single saved artifact. Reached from the Results view on
 * an automation's Results tab and from the notification email's "View report" link.
 * Artifacts are editor-only, so it resolves everything it needs from the
 * automation + artifact ids in the URL.
 */
export const AutomationArtifactDetailPage: FC = () => {
  const { playbookId, automationId, artifactId } = useParams();
  const navigate = useNavigate();

  const parsedAutomationId = automationId
    ? automationIdSchema.safeParse(automationId)
    : undefined;
  const parsedArtifactId = artifactId
    ? automationArtifactIdSchema.safeParse(artifactId)
    : undefined;

  const { data: automation } = useAutomationByIdQuery(
    parsedAutomationId?.success ? parsedAutomationId.data : skipToken,
  );
  const { data: artifact, isLoading: isArtifactLoading } =
    useAutomationArtifactByIdQuery(
      parsedAutomationId?.success && parsedArtifactId?.success
        ? {
            automationId: parsedAutomationId.data,
            automationArtifactId: parsedArtifactId.data,
          }
        : skipToken,
    );
  const { data: teammates } = useAllTeammatesQuery();

  const teammateNamesById = useMemo(
    () =>
      new Map<TeammateId, string>(
        (teammates ?? []).map((teammate) => [
          teammate.id,
          renderTeammateDisplayName(teammate),
        ]),
      ),
    [teammates],
  );

  const recipientNamesByType = useMemo(() => {
    const namesByType = new Map<AutomationArtifactRecipientType, string[]>();
    for (const recipient of artifact?.recipients ?? []) {
      const names = namesByType.get(recipient.type) ?? [];
      names.push(teammateNamesById.get(recipient.teammate_id) ?? "A teammate");
      namesByType.set(recipient.type, names);
    }
    return namesByType;
  }, [artifact?.recipients, teammateNamesById]);

  if (!parsedAutomationId?.success || !parsedArtifactId?.success) {
    return <Navigate to={appPaths.home.build()} replace />;
  }

  if (isArtifactLoading) {
    return (
      <div className="flex min-h-full items-center justify-center">
        <Loader style={LoaderStyle.ZOOMIES} />
      </div>
    );
  }

  if (!artifact) {
    return <Navigate to={appPaths.home.build()} replace />;
  }

  const automationsHref = playbookId
    ? appPaths.playbookAutomations.build({ playbookId })
    : undefined;
  const automationHref =
    automationsHref && automation
      ? `${automationsHref}/${encodeURIComponent(automation.id)}`
      : undefined;
  const runHref = automationHref
    ? `${automationHref}/runs/${encodeURIComponent(artifact.automation_run_id)}`
    : undefined;

  // Who and where the report went: its To recipients, then the channels it's
  // posted to (their names are stored as configured, already carrying the
  // provider's prefix, e.g. "# reports").
  const sentToNames = [
    ...(recipientNamesByType.get("to") ?? []),
    ...artifact.channels.map((channel) => channel.channel_name ?? "A channel"),
  ];

  const eyebrowText = [
    sentToNames.length > 0 ? sentToNames.join(", ") : null,
    formatRunTime(artifact.created_at),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <PageLayout>
      <PageHeader
        width={PageWidth.Standard}
        eyebrow={eyebrowText}
        title={artifact.title}
        actions={
          runHref ? (
            <Button
              variant={ButtonVariant.Secondary}
              size={Size.SMALL}
              onClick={() => {
                navigate(runHref);
              }}
            >
              Show run
            </Button>
          ) : undefined
        }
      />

      <PageBody width={PageWidth.Standard}>
        <article className="w-full rounded-lg border border-border-light bg-white p-6">
          <GradingReferenceProvider value={GradingReferenceLinkAdapter}>
            <PlaybookFeedbackReferenceProvider
              value={PlaybookFeedbackReferenceLinkAdapter}
            >
              <AutomationArtifactContent artifact={artifact} />
            </PlaybookFeedbackReferenceProvider>
          </GradingReferenceProvider>
        </article>
      </PageBody>
    </PageLayout>
  );
};
